// src/components/ProfileForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { UserProfile } from '../types';
import '../styles/components/ProfileForm.css';

const ProfileForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    level: '' as UserProfile['level'],
    area_of_study: '',
    country: '',
    state: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setError('User not authenticated');
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile:', profileError);
          return;
        }

        if (profile) {
          setFormData({
            name: profile.name || '',
            level: profile.level || '',
            area_of_study: profile.area_of_study || '',
            country: profile.country || '',
            state: profile.state || ''
          });
        }
      } catch (err: any) {
        console.error('Error in fetchUserProfile:', err);
        setError('Failed to load profile data');
      }
    };

    fetchUserProfile();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        setError('User not authenticated. Please login again.');
        setLoading(false);
        return;
      }

      const { error: upsertError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          name: formData.name,
          level: formData.level,
          area_of_study: formData.area_of_study,
          country: formData.country,
          state: formData.state,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        });

      if (upsertError) {
        console.error('Supabase error:', upsertError);
        setError(`Failed to save profile: ${upsertError.message}`);
        setLoading(false);
        return;
      }

      // Navigate to dashboard on successful save
      navigate('/dashboard');

    } catch (err: any) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="profile-form-container">
      <div className="profile-form-card">
        <h2>Let's Get to Know You</h2>
        <p className="form-subtitle">Complete your profile to get personalized tutoring</p>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="level">Academic Level *</label>
            <select
              id="level"
              name="level"
              value={formData.level}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">Select your academic level</option>
              <option value="secondary">Secondary School</option>
              <option value="undergraduate">Undergraduate</option>
              <option value="postgraduate">Postgraduate</option>
              <option value="researcher">Researcher</option>
              <option value="professional">Professional</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="area_of_study">Area of Study *</label>
            <input
              type="text"
              id="area_of_study"
              name="area_of_study"
              value={formData.area_of_study}
              onChange={handleChange}
              required
              placeholder="e.g., Computer Science, Biology, Medicine, etc."
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Country *</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              placeholder="Enter your country"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="state">State/Region *</label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
              placeholder="Enter your state or region"
              disabled={loading}
            />
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="submit-button"
          >
            {loading ? 'Saving...' : 'Save Profile & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileForm;